import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import {
  BLASTFURNACE_TEMPLATE_ID, BLASTFURNACE_SVG,
  BLASTFURNACE_V2_TEMPLATE_ID, BLASTFURNACE_V2_SVG,
} from '../fallback/blastfurnaceTemplate';

// DOMPurify config that keeps SVG intact but strips scripts/handlers.
const SANITIZE_OPTS = {
  USE_PROFILES: { svg: true, svgFilters: true },
  ADD_ATTR: ['data-section'],
};

export function sanitizeSvg(raw) {
  return DOMPurify.sanitize(raw, SANITIZE_OPTS);
}

// FE-bundled fallback templates: markup ships in the app, svgUrl points at a
// same-origin /public file (not an http(s) BE/CDN url).
const BUNDLED_SVG = {
  [BLASTFURNACE_TEMPLATE_ID]: BLASTFURNACE_SVG,
  [BLASTFURNACE_V2_TEMPLATE_ID]: BLASTFURNACE_V2_SVG,
};

function isBundledTemplate(id, svgUrl) {
  return id in BUNDLED_SVG && !svgUrl?.startsWith('http');
}

/**
 * Fetches the SVG markup for a template, sanitizes it, and returns the safe
 * string for inlining.
 *
 * Fallback policy: the bundled blast-furnace markup is used ONLY for the bundled
 * fallback template. For a real BE template, a failed `svgUrl` fetch (CORS, 404,
 * network) is surfaced as a query error — it is NOT silently replaced with the
 * bundled SVG, so a broken BE template can't masquerade as a working one.
 *
 * @param {{ id?: string, svgUrl?: string }|null} template
 */
export function useSvgMarkup(template) {
  const id = template?.id ?? null;
  const svgUrl = template?.svgUrl ?? null;

  return useQuery({
    queryKey: ['svg-markup', id, svgUrl],
    enabled: !!template,
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: async () => {
      // Bundled fallback template — try the static public file, else inlined string.
      if (isBundledTemplate(id, svgUrl)) {
        try {
          if (svgUrl) {
            const res = await fetch(svgUrl);
            if (res.ok) return sanitizeSvg(await res.text());
          }
        } catch {
          // ignore — use the inlined bundled string
        }
        return sanitizeSvg(BUNDLED_SVG[id]);
      }

      // Real BE template: it MUST have a usable svgUrl.
      if (!svgUrl) {
        throw new Error('This diagram template has no svgUrl. Ask the backend to include it.');
      }

      let res;
      try {
        res = await fetch(svgUrl);
      } catch {
        // fetch() rejects on CORS / network failures with an opaque TypeError.
        throw new Error(
          `Could not load the diagram SVG (network or CORS). The SVG host must send `
          + `Access-Control-Allow-Origin for this app's origin. URL: ${svgUrl}`,
        );
      }
      if (!res.ok) {
        throw new Error(`Could not load the diagram SVG — HTTP ${res.status}. URL: ${svgUrl}`);
      }
      return sanitizeSvg(await res.text());
    },
  });
}
